import { Info } from "@mui/icons-material";
import { Box, Divider, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { useTranslation } from "react-i18next";

import { useMortgageState } from "../../../contexts/mortgage/Reducer";
import { addYears, DateFormat, formatDate } from "../../../utils/DateUtils";
import { formatAmount, formatInterestRate, parseIBAN } from "../../../utils/NumberUtils";
import { renderProductType } from "../../../utils/Utils";
import ErrorMessage from "../../feedback/ErrorMessage";
import MortgageTooltip from "../../tooltip/MortgageTooltip";

export const MortgageSummaryHeaderLabel = styled(Typography)(() => ({
    color: "rgba(0,0,0,0.54)",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: 0,
    lineHeight: "18px",
    whiteSpace: "nowrap",
}));

export const MortgageSummaryBold = styled(Typography)(() => ({
    color: "#333333",
    fontSize: "18px",
    letterSpacing: 0,
    lineHeight: "30px",
    fontWeight: 500,
}));

export const MortgageSummaryLabel = styled(Typography)(() => ({
    color: "#333333",
    fontSize: "18px",
    letterSpacing: 0,
    lineHeight: "26px",
    fontWeight: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

export const MortgageSummaryLabelSmall = styled(Typography)(() => ({
    color: "rgba(0,0,0,0.54)",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: 0,
    lineHeight: "18px",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

const NewMortgageSummary = () => {
    const { i18n, t } = useTranslation();

    const theme = useTheme();
    const matchesWebView = useMediaQuery(theme.breakpoints.up("lg"));

    const { newMortgage } = useMortgageState();

    if (newMortgage === undefined) {
        return <ErrorMessage />;
    }

    const renderWebView = () => {
        return (
            <TableContainer component={Box} sx={{ overflowX: "hidden" }}>
                <Table aria-label="new mortgage table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <MortgageSummaryHeaderLabel data-cy={"new-financing-label"}>
                                    {t("SummaryScreen.Financing")}
                                </MortgageSummaryHeaderLabel>
                            </TableCell>
                            <TableCell>
                                <MortgageSummaryHeaderLabel data-cy={"new-term-label"}>
                                    {t("SummaryScreen.Term")}
                                </MortgageSummaryHeaderLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryHeaderLabel data-cy={"new-amount-in-label"}>
                                    {t("General.Amount in", { currency: newMortgage.currency })}
                                </MortgageSummaryHeaderLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryHeaderLabel data-cy={"new-interest-rate-label"}>
                                    {t("General.Interest rate")}
                                </MortgageSummaryHeaderLabel>
                            </TableCell>
                            <TableCell align="right">
                                <Box display={"flex"} justifyContent={"flex-end"}>
                                    <MortgageSummaryHeaderLabel data-cy={"new-monthly-charge-label"}>
                                        {t("General.Monthly charge in", { currency: newMortgage.currency })}
                                    </MortgageSummaryHeaderLabel>
                                    <MortgageTooltip
                                        title={t("General.This calculation is indicative and is based on...")}>
                                        <Info sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                                    </MortgageTooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow key="fixed-mortgage" sx={{ height: "87px" }}>
                            <TableCell component="th" scope="row">
                                <MortgageSummaryBold data-cy={"new-product-type-label"}>
                                    {renderProductType(newMortgage, i18n, t)}
                                </MortgageSummaryBold>
                            </TableCell>
                            <TableCell>
                                <MortgageSummaryLabel data-cy={"new-duration"}>
                                    {" "}
                                    {newMortgage.duration} {t("General.Years")}
                                </MortgageSummaryLabel>
                                <MortgageSummaryLabelSmall data-cy={"new-expiration-of-mortgage"}>
                                    {formatDate(newMortgage.expirationDate, DateFormat.DATE)} -{" "}
                                    {addYears(newMortgage.expirationDate, +newMortgage.duration)}
                                </MortgageSummaryLabelSmall>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"new-mortgage-amount"}>
                                    {" "}
                                    {formatAmount(newMortgage.amount)}{" "}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"new-mortgage-interest-rate"}>
                                    {formatInterestRate(newMortgage.interestRate)} %
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"new-mortgage-monthly-payment"}>
                                    {formatAmount(newMortgage.monthlyPayment)}
                                </MortgageSummaryLabel>
                            </TableCell>
                        </TableRow>

                        <TableRow key="repayment" sx={{ height: "87px" }}>
                            <TableCell component="th" scope="row">
                                <MortgageSummaryBold data-cy={"new-repayment-label"}>
                                    {t("General.Repayment")}
                                </MortgageSummaryBold>
                                <MortgageSummaryLabelSmall data-cy={"new-from-account-label"}>
                                    {t("General.From account iban", {
                                        account: newMortgage.positionCapital,
                                        iban: parseIBAN(newMortgage.ibanCapital),
                                    })}
                                </MortgageSummaryLabelSmall>
                            </TableCell>
                            <TableCell>
                                <MortgageSummaryLabel data-cy={"summary-expiration-date"}>
                                    {formatDate(newMortgage.expirationDate, DateFormat.DATE)}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"new-mortgage-repayment"}>
                                    {formatAmount(newMortgage.repayment)}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderMobileView = () => {
        return (
            <Box>
                <Box width={"100%"}>
                    <Box>
                        <MortgageSummaryBold>{renderProductType(newMortgage, i18n, t)}</MortgageSummaryBold>
                    </Box>
                    <Box sx={{ marginBottom: "16px" }}>
                        <MortgageSummaryLabel>
                            {newMortgage.duration} {t("General.Years")} /{" "}
                            {formatDate(newMortgage.expirationDate, DateFormat.DATE)} -{" "}
                            {addYears(newMortgage.expirationDate, +newMortgage.duration)}
                        </MortgageSummaryLabel>
                    </Box>
                    <Box display={"flex"}>
                        <Box flex={1}>
                            <Box>
                                <MortgageSummaryHeaderLabel>{t("General.Monthly charge")}</MortgageSummaryHeaderLabel>
                            </Box>
                            <Box display={"flex"} alignItems={"center"}>
                                <MortgageSummaryLabel>
                                    {newMortgage.currency} {formatAmount(newMortgage.monthlyPayment)}
                                </MortgageSummaryLabel>
                                <MortgageTooltip title={t("General.This calculation is indicative and is based on...")}>
                                    <Info sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                                </MortgageTooltip>
                            </Box>
                        </Box>
                        <Box flex={1}>
                            <Box>
                                <MortgageSummaryHeaderLabel>{t("General.Interest rate")}</MortgageSummaryHeaderLabel>
                            </Box>
                            <Box>
                                <MortgageSummaryLabel>{formatAmount(newMortgage.interestRate)} %</MortgageSummaryLabel>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ marginTop: "16px", marginBottom: "16px", display: "flex", flexFlow: "row-reverse" }}>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.amount)}
                        </MortgageSummaryBold>
                    </Box>
                </Box>
                <Divider />
                <Box sx={{ paddingTop: "16px" }}>
                    <Box>
                        <MortgageSummaryBold>{t("General.Repayment")}</MortgageSummaryBold>
                    </Box>
                    <Box>
                        <MortgageSummaryLabel>
                            {t("General.From account", { account: newMortgage.positionCapital })}
                        </MortgageSummaryLabel>
                    </Box>
                    <Box>
                        <MortgageSummaryLabel>{parseIBAN(newMortgage.ibanCapital)}</MortgageSummaryLabel>
                    </Box>
                    <Box sx={{ marginTop: "16px", marginBottom: "16px", display: "flex", flexFlow: "row-reverse" }}>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.repayment)}
                        </MortgageSummaryBold>
                    </Box>
                </Box>
                <Divider />
            </Box>
        );
    };

    return <Box data-cy={"new-mortgage-summary"}>{matchesWebView ? renderWebView() : renderMobileView()}</Box>;
};
export default NewMortgageSummary;
